import { getStoragePrefix } from './domain'

// const TokenKey = 'Authorization' // 注释掉未使用的变量
// 方案二已移除CSRF功能，不再需要相关变量

// 状态同步事件键名
const AUTH_EVENT_KEY = 'auth_state_change'
const AUTH_EVENT_LOGOUT = 'logout'
// Token相关常量
const TOKEN_KEY = `${getStoragePrefix()}_token`
const USER_INFO_KEY = `${getStoragePrefix()}_user_info`

/**
 * 处理 Bearer 前缀
 * @param {string} token - 原始token
 * @returns {string} 处理后的token
 */

/**
 * 移除 Bearer 前缀
 * @param {string} token - 带前缀的token
 * @returns {string} 不带前缀的token
 */
function removeBearerPrefix(token) {
  if (!token) return null;
  return token.startsWith('Bearer ') ? token.substring(7) : token;
}

/**
 * 验证token格式和有效期
 * @param {string} token - 要验证的token
 * @returns {boolean} 是否有效
 */
function validateTokenFormat(token) {
  if (!token) return false;
  
  try {
    const actualToken = removeBearerPrefix(token);
    const tokenParts = actualToken.split('.');
    
    if (tokenParts.length !== 3) {
      console.warn('Token格式无效');
      return false;
    }
    
    const payload = JSON.parse(atob(tokenParts[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      console.warn('Token已过期');
      return false;
    }
    
    return true;
  } catch (e) {
    console.error('Token验证失败:', e);
    return false;
  }
}

/**
 * 获取token（用于API请求）
 * @returns {string|null} 带Bearer前缀的token或null
 */
export function getToken() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  
  // 如果存储的 token 已经带有 Bearer 前缀，直接返回
  if (token.startsWith('Bearer ')) {
    return validateTokenFormat(token.substring(7)) ? token : null;
  }
  
  // 如果存储的 token 没有 Bearer 前缀，验证并添加
  return validateTokenFormat(token) ? `Bearer ${token}` : null;
}

/**
 * 获取原始token（不带Bearer前缀）
 * @returns {string|null} 不带Bearer前缀的token或null
 */
export function getRawToken() {
  const token = localStorage.getItem(TOKEN_KEY);
  return token && validateTokenFormat(token) ? removeBearerPrefix(token) : null;
}

/**
 * 设置token
 * @param {string} token - token字符串
 */
export function setToken(token) {
  if (!token) {
    console.warn('尝试设置空token');
    return;
  }
  
  // 确保存储的 token 不带 Bearer 前缀
  const rawToken = removeBearerPrefix(token);
  
  // 验证 token 格式
  if (!validateTokenFormat(rawToken)) {
    console.warn('Token格式无效，未保存');
    return;
  }
  
  // 存储时添加 Bearer 前缀
  localStorage.setItem(TOKEN_KEY, `Bearer ${rawToken}`);
  console.log('Token已保存（带Bearer前缀）');
}

/**
 * 移除token
 */
export function removeToken() {
  localStorage.removeItem(TOKEN_KEY);
  console.log('Token已移除');
}

// 设置认证相关的cookie
export function setAuthCookie(name, value) {
  // 获取主域名 (.ge120.cn) 以支持二级域名共享
  const domain = process.env.NODE_ENV === 'production' ? '.ge120.cn' : undefined;
  const secure = process.env.NODE_ENV === 'production';
  const sameSite = process.env.NODE_ENV === 'production' ? 'none' : 'lax';
  
  // 计算过期时间 (24小时)
  const expires = new Date();
  expires.setTime(expires.getTime() + 24 * 60 * 60 * 1000);
  
  // 设置cookie
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; ${domain ? `domain=${domain};` : ''} ${secure ? 'secure;' : ''} samesite=${sameSite}`;
  
  console.log(`已设置${name} cookie, domain=${domain}, secure=${secure}, expires=${expires.toUTCString()}`);
}

// 移除认证相关的cookie
export function removeAuthCookie(name) {
  const domain = getDomainForCookie();
  
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain};`;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
  
  console.log(`已移除${name} cookie, domain=${domain}`);
}

// 获取用于cookie的域名
export function getDomainForCookie() {
  const hostname = window.location.hostname;
  
  // 检查是否是IP地址
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
    return hostname; // IP地址直接返回
  }
  
  // 匹配域名部分 (例如从wx.ge120.cn提取.ge120.cn)
  const parts = hostname.split('.');
  if (parts.length > 2) {
    // 返回主域和顶级域 (.ge120.cn)
    return '.' + parts.slice(-2).join('.');
  } else {
    // 如果只有两部分或更少 (localhost或者单域名)，直接返回
    return hostname;
  }
}

// 清除所有认证状态
export function clearAllAuthState() {
  removeToken()
  localStorage.removeItem(USER_INFO_KEY)
  console.log('所有认证状态已清除')
}

// 清除所有认证相关cookie
export function clearAuthCookies() {
  const cookies = document.cookie.split(';');
  const domain = getDomainForCookie();
  const path = '/';
  
  // 删除所有可能的认证相关cookie
  const authCookies = ['access_token', 'refresh_token', 'token', 'auth_token', 'session', 'user'];
  
  console.log('开始清除认证相关cookie...');
  
  for (const cookie of cookies) {
    const [name] = cookie.trim().split('=');
    
    // 尝试识别认证相关cookie
    const isAuthCookie = authCookies.some(prefix => 
      name.toLowerCase().includes(prefix.toLowerCase())
    );
    
    if (isAuthCookie) {
      console.log(`删除认证相关cookie: ${name}`);
      
      // 使用多种路径和域名组合删除cookie
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path};`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain};`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain};`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
    }
  }
  
  console.log('清除cookie完成');
}

// 向其他标签页广播认证事件
export function broadcastAuthEvent(eventType) {
  try {
    localStorage.setItem(AUTH_EVENT_KEY, JSON.stringify({
      type: eventType,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('广播认证事件失败:', error);
  }
}

// 监听其他标签页的认证事件
export function listenToAuthEvents(logoutCallback) {
  window.addEventListener('storage', (event) => {
    if (event.key === AUTH_EVENT_KEY) {
      try {
        const data = JSON.parse(event.newValue);
        if (data && data.type === AUTH_EVENT_LOGOUT && typeof logoutCallback === 'function') {
          console.log('检测到其他标签页登出事件，执行本地登出逻辑');
          logoutCallback();
        }
      } catch (error) {
        console.error('处理认证事件失败:', error);
      }
    }
  });
  
  console.log('已设置跨标签页认证事件监听');
}

// 检查token是否已过期
export function isTokenExpired() {
  const token = getRawToken();
  if (!token) return true;
  
  const decoded = decodeToken(token);
  return !decoded || decoded.isExpired;
}

// 验证token有效性
export async function validateToken() {
  const token = getRawToken();
  return validateTokenFormat(token);
}

// CSRF相关函数已移除 - 方案二不再需要
// 为了保持兼容性，提供空函数
export function getCsrfToken() {
  console.log('CSRF功能已移除，返回null');
  return null;
}

export function getCsrfHeader() {
  console.log('CSRF功能已移除，返回空对象');
  return {};
}

export function resetCsrfTokenCache() {
  console.log('CSRF功能已移除，此函数不执行任何操作');
}

// 用户信息管理
export function getUserInfo() {
  const info = localStorage.getItem(USER_INFO_KEY);
  try {
    return info ? JSON.parse(info) : null;
  } catch (e) {
    console.error('解析用户信息失败:', e);
    return null;
  }
}

export function setUserInfo(userInfo) {
  localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
}

export function removeUserInfo() {
  localStorage.removeItem(USER_INFO_KEY);
}

/**
 * 获取登录后的重定向URL
 * @param {string|null} urlRedirect - URL参数中的redirect值
 * @param {string} defaultPath - 默认路径，如果没有重定向URL
 * @returns {string} 最终的重定向URL
 */
export function getRedirectUrl(urlRedirect, defaultPath) {
  // 从环境变量获取默认路径，如果没有则使用传入的defaultPath或'/office/desktop'
  const envDefaultPath = process.env.VUE_APP_DEFAULT_LOGIN_REDIRECT || defaultPath || '/office/desktop'
  const storedRedirect = localStorage.getItem('loginRedirect')
  
  // 优先级：URL参数 > localStorage > 环境变量默认值
  const redirectUrl = urlRedirect || storedRedirect || envDefaultPath
  
  // 如果使用了localStorage中的值，使用后需要清除
  if (storedRedirect && !urlRedirect) {
    localStorage.removeItem('loginRedirect')
    console.log('已清除localStorage中的loginRedirect')
  }
  
  console.log('获取重定向URL:', {
    urlRedirect,
    storedRedirect,
    envDefaultPath,
    finalRedirect: redirectUrl,
    env: process.env.VUE_APP_DEFAULT_LOGIN_REDIRECT
  })
  
  return redirectUrl
}

// 登录状态检查
export function isLoggedIn() {
  return !!getToken() && !!getUserInfo();
}

// 添加一个工具函数来解析token
export function decodeToken(token) {
  if (!token) return null;
  
  try {
    const rawToken = removeBearerPrefix(token);
    const tokenParts = rawToken.split('.');
    if (tokenParts.length !== 3) return null;
    
    const payload = JSON.parse(atob(tokenParts[1]));
    return {
      header: JSON.parse(atob(tokenParts[0])),
      payload,
      signature: tokenParts[2],
      expiresAt: new Date(payload.exp * 1000),
      issuedAt: new Date(payload.iat * 1000),
      isExpired: payload.exp * 1000 < Date.now()
    };
  } catch (e) {
    console.error('解析token失败:', e);
    return null;
  }
} 